import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Breadcrumbs,
  Link,
  IconButton,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAsyncOperation } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import { ClientForm } from '../../types';

interface CreateClientPageProps {}

const CreateClientPage: React.FC<CreateClientPageProps> = () => {
  const navigate = useNavigate();
  const [clientData, setClientData] = useState<ClientForm>({
    type: 'individual',
    name: '',
    email: '',
    phoneNumber: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'República Dominicana',
    },
    businessName: '',
    taxId: '',
    contacts: [
      {
        name: '',
        position: '',
        phone: '',
        email: '',
        isPrimary: true,
      }
    ],
    notes: '',
  });

  const { execute: createClient, loading: creating } = useAsyncOperation();

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setClientData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof ClientForm] as any),
          [child]: value,
        }
      }));
    } else {
      setClientData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleContactChange = (index: number, field: string, value: string) => {
    const updatedContacts = [...clientData.contacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value,
    };
    setClientData(prev => ({
      ...prev,
      contacts: updatedContacts,
    }));
  };

  const addContact = () => {
    setClientData(prev => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        {
          name: '',
          position: '',
          phone: '',
          email: '',
          isPrimary: false,
        }
      ]
    }));
  };

  const removeContact = (index: number) => {
    if (clientData.contacts.length > 1) {
      const updatedContacts = clientData.contacts.filter((_, i) => i !== index);
      setClientData(prev => ({
        ...prev,
        contacts: updatedContacts,
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      const result = await createClient(() => apiService.createClient(clientData));
      if (result) {
        navigate('/clients');
      }
    } catch (error) {
      console.error('Error creating client:', error);
    }
  };

  const isFormValid = () => {
    return clientData.name &&
           clientData.email &&
           clientData.phoneNumber &&
           clientData.contacts.some(contact => contact.name);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/clients')}
          >
            Clientes
          </Link>
          <Typography variant="body2" color="text.primary">
            Nuevo Cliente
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Crear Nuevo Cliente
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Completa la información para registrar un nuevo cliente
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/clients')}
          >
            Volver
          </Button>
        </Box>
      </Box>

      {/* Form */}
      <Card>
        <CardContent>
          <Stack spacing={4}>
            {/* Client Type */}
            <FormControl fullWidth>
              <InputLabel>Tipo de Cliente</InputLabel>
              <Select
                value={clientData.type}
                label="Tipo de Cliente"
                onChange={(e) => handleInputChange('type', e.target.value)}
              >
                <MenuItem value="individual">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon />
                    Individual
                  </Box>
                </MenuItem>
                <MenuItem value="business">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon />
                    Empresa
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Basic Information */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Información Básica
              </Typography>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Nombre Completo"
                  value={clientData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />

                {clientData.type === 'business' && (
                  <>
                    <TextField
                      fullWidth
                      label="Nombre de la Empresa"
                      value={clientData.businessName}
                      onChange={(e) => handleInputChange('businessName', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      label="RNC/Cédula"
                      value={clientData.taxId}
                      onChange={(e) => handleInputChange('taxId', e.target.value)}
                    />
                  </>
                )}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={clientData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={clientData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    required
                  />
                </Stack>
              </Stack>
            </Box>

            <Divider />

            {/* Address */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Dirección
              </Typography>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Dirección"
                  value={clientData.address.street}
                  onChange={(e) => handleInputChange('address.street', e.target.value)}
                />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Ciudad"
                    value={clientData.address.city}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Estado/Provincia"
                    value={clientData.address.state}
                    onChange={(e) => handleInputChange('address.state', e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Código Postal"
                    value={clientData.address.zipCode}
                    onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                  />
                </Stack>
                <TextField
                  fullWidth
                  label="País"
                  value={clientData.address.country}
                  onChange={(e) => handleInputChange('address.country', e.target.value)}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Contacts */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Contactos
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addContact}
                >
                  Agregar Contacto
                </Button>
              </Box>

              <Stack spacing={3}>
                {clientData.contacts.map((contact, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Contacto {index + 1} {contact.isPrimary && '(Principal)'}
                        </Typography>
                        {!contact.isPrimary && clientData.contacts.length > 1 && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeContact(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                      <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            fullWidth
                            label="Nombre"
                            value={contact.name}
                            onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                            required={contact.isPrimary}
                          />
                          <TextField
                            fullWidth
                            label="Posición/Cargo"
                            value={contact.position}
                            onChange={(e) => handleContactChange(index, 'position', e.target.value)}
                          />
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            fullWidth
                            label="Teléfono"
                            value={contact.phone}
                            onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                          />
                          <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={contact.email}
                            onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                          />
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>

            <Divider />

            {/* Notes */}
            <TextField
              fullWidth
              label="Notas"
              multiline
              rows={3}
              value={clientData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Información adicional sobre el cliente..."
            />

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/clients')}
                disabled={creating}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={!isFormValid() || creating}
              >
                {creating ? 'Guardando...' : 'Crear Cliente'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateClientPage;